// CourseForm.tsx
import { Stack, Text, Textarea, Switch, Box, ActionIcon, Image, Center } from '@mantine/core';
import { IconPhoto, IconX } from '@tabler/icons-react';
import { TextInput, ColorInput } from '@/components';
import { FileUploader } from '@/components/ccms-components/file-management';
import { toast } from '@/components/ui/providers/AlertProvider';
import type { CreateACCourse } from '@actschurch/ccms-sdk';

export interface CourseFormState extends CreateACCourse {
  isActive: boolean;
}

export const EMPTY_COURSE_FORM: CourseFormState = {
  name: '',
  description: '',
  imageURL: '',
  color: '',
  statusID: 1,
  isActive: true,
};

// ASSUMPTION — matches the CompletedUpload shape used in DocumentTab.tsx
interface CompletedUpload {
  url: string;
  fileName: string;
  mimeType?: string;
}

interface CourseFormProps {
  form: CourseFormState;
  onChange: (form: CourseFormState) => void;
  // Used as the FileUploader recordId. Pass 'new' while a course doesn't
  // exist yet (Add flow); pass the real courseID once it does (Edit flow).
  uploadRecordId: string;
}

export default function CourseForm({ form, onChange, uploadRecordId }: CourseFormProps) {
  const set = (patch: Partial<CourseFormState>) => onChange({ ...form, ...patch });

  const handleImageUploadComplete = (files: CompletedUpload[]) => {
    const uploaded = files[0];
    if (!uploaded) return;
    set({ imageURL: uploaded.url });
    toast.success('Image Uploaded', `${uploaded.fileName} was set as the course image.`);
  };

  return (
    <Stack>
      <TextInput
        label="Course Name"
        placeholder="Enter course name"
        value={form.name ?? ''}
        onChange={(e) => set({ name: e.currentTarget.value })}
        required
      />

      <Textarea
        label="Description"
        placeholder="Enter a brief description"
        value={form.description ?? ''}
        onChange={(e) => set({ description: e.currentTarget.value })}
        minRows={3}
      />

       {/* ── Color — themed ColorInput ────────────────────────────────────────── */}
      <ColorInput
        label="Course Colour"
        placeholder="Pick a colour"
        value={form.color || ''}
        onChange={(val) => set({ color: val })}
        format="hex"
      />

      <Switch
        label="Active"
        checked={form.isActive}
        onChange={(e) => set({ isActive: e.currentTarget.checked, statusID: e.currentTarget.checked ? 1 : 0 })}
      />

      {/* ── Course Image — upload instead of typing a URL ────────────────────── */}
      <Stack gap={6}>
        <Text size="sm" fw={500}>Course Image</Text>

        {form.imageURL ? (
          <Box style={{ position: 'relative', width: 'fit-content' }}>
            <Image
              src={form.imageURL}
              alt="Course image"
              radius="md"
              h={140}
              w={370}
              fit="cover"
            />
            <ActionIcon
              variant="filled"
              color="red"
              size="sm"
              radius="xl"
              style={{ position: 'absolute', top: 6, right: 6 }}
              onClick={() => set({ imageURL: '' })}
            >
              <IconX size={14} />
            </ActionIcon>
          </Box>
        ) : (
          <Center
            h={140}
            w={370}
            style={(theme) => ({
              border: `1px dashed ${theme.colors.gray[5]}`,
              borderRadius: theme.radius.md,
              color: theme.colors.gray[6],
            })}
          >
            <Stack gap={4} align="center">
              <IconPhoto size={28} />
              <Text size="xs" c="dimmed">No image selected</Text>
            </Stack>
          </Center>
        )}

        <FileUploader
          domain="Course"
          recordId={uploadRecordId}
          multiple={false}
          accept={['image/*']}
          onUploadComplete={handleImageUploadComplete}
          onUploadError={(err: any) => {
            console.error(err);
            toast.error('Upload Failed', err?.message ?? 'Could not upload image.');
          }}
        />
      </Stack>

     
    </Stack>
  );
}